import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import MenuItem from '@mui/material/MenuItem';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { red } from '@mui/material/colors';
import ListItemIcon from '@mui/material/ListItemIcon';
import IconMenu from 'components/IconMenu';
import ModalForm from 'components/Form/ModalForm';
import { capitalize } from 'helpers/helpers';
import { DashboardFolderT } from 'business/definitions';
import { useUserHasRole } from 'hooks/client';
import { ADMIN } from 'business/constants';
import { useRenameFolder } from './hooks/renameFolder';
import { useDeleteFolder } from './hooks/deleteFolder';

type ActionType = 'rename' | 'delete';

type DashboardFolderEditMenuProps = {
    dashboardFolder: DashboardFolderT;
    show?: boolean;
    small?: boolean;
};

const DashboardFolderEditMenu = ({
    dashboardFolder,
    show = false,
    small = false,
}: DashboardFolderEditMenuProps): JSX.Element => {
    const { t } = useTranslation();

    const userIsAdmin = useUserHasRole(ADMIN);
    const [selectedAction, setSelectedAction] = useState<ActionType | null>(
        null,
    );

    const closeMenu = () => setSelectedAction(null);

    const renameFolderMutation = useRenameFolder();
    const deleteFolderMutation = useDeleteFolder(dashboardFolder.id);

    if (!show) return <></>;
    if (!userIsAdmin) return <></>;

    const renameDashboardFolder = ({ name }: { name: string }) => {
        if (name !== dashboardFolder.name && dashboardFolder.id !== undefined) {
            renameFolderMutation.mutate({ name, id: dashboardFolder.id });
        }
        closeMenu();
    };
    const deleteDashboardFolder = () => {
        deleteFolderMutation.mutate();
        closeMenu();
    };

    const onClickModal = (name: ActionType | null) => () => {
        setSelectedAction(name);
    };

    const actions: ActionType[] = ['rename', 'delete'];
    const icons = {
        rename: <EditIcon />,
        delete: <DeleteIcon />,
    };

    return (
        <div className="header-options-wrapper">
            <IconMenu
                iconElement={<MoreVertIcon />}
                tooltip={capitalize(t('common.options'))}
                tooltipPlacement="bottom"
                small={small}
                className="e2e-dashboard-options-menu"
                data-testid="folder-edit-menu"
            >
                {actions.map((action) => (
                    <MenuItem
                        key={action}
                        onClick={onClickModal(action)}
                        className={`e2e-dashboard-${action}-button`}
                    >
                        <ListItemIcon>{icons[action]}</ListItemIcon>
                        {capitalize(t(`common.${action}`))}
                    </MenuItem>
                ))}
            </IconMenu>

            {selectedAction === 'rename' && (
                <ModalForm
                    show
                    closeModal={closeMenu}
                    // It enables renaming the folders containing dashboards.
                    isClickOutsideEnabled={false}
                    submit={renameDashboardFolder}
                    submitText={t('common.save')}
                    title={t('dashboardFolders.update')}
                    fields={[
                        {
                            key: 'name',
                            name: 'name',
                            label: t('common.title'),
                            type: 'text',
                            value: dashboardFolder.name,
                            validators: ['required'],
                        },
                    ]}
                    submitClass="e2e-modal-update-dashboard-button"
                />
            )}

            {selectedAction === 'delete' && (
                <ModalForm
                    show
                    closeModal={closeMenu}
                    submit={deleteDashboardFolder}
                    submitText={t('common.delete')}
                    title={t('common.deleteConfirm')}
                    secondary={t('dashboardFolders.deleteWarning')}
                    confirmButtonColor={red[900]}
                    submitClass="e2e-delete-dashboard-button"
                />
            )}
        </div>
    );
};

export default DashboardFolderEditMenu;
